import api from 'api';
import { isUserAccountLockedOrExpired } from 'shared-helpers/common';

import type { Router } from 'next/router';
import type { UserWithBasicData } from 'shared-types';
import type { PageState } from 'types';

export type UserSelectionParams = Partial<
  Pick<PageState, 'courseAccess' | 'search'>
> & {
  allowAnonymousUsers?: boolean;
  allowGuestUsers?: boolean;
  courseAccess?: 'all' | 'withAccess' | 'withoutAccess';
  excludeInvalidAccounts?: boolean;
  excludeUsersWithEmail?: boolean;
  excludeUsersWithoutEmail?: boolean;
  routerQueryParams?: Router['query'];
  selectedUserIds?: number[];
  userSelector: 'emails' | 'usernames';
  supervisorRouteSupervisorId?: number;
};

const getSelectedUsers = async ({
  allowAnonymousUsers = false,
  allowGuestUsers = false,
  courseAccess,
  excludeInvalidAccounts = false,
  excludeUsersWithEmail = false,
  excludeUsersWithoutEmail = false,
  selectedUserIds,
  search,
  routerQueryParams,
  userSelector,
  supervisorRouteSupervisorId
}: UserSelectionParams) => {
  try {
    let baseUrl = '/admin/users-admin-emails?';

    if (supervisorRouteSupervisorId) {
      baseUrl = `/admin/users-supervisor-emails/${supervisorRouteSupervisorId}?`;
    }

    let queryParams = `allowGuestUsers=${allowGuestUsers}&allowAnonymousUsers=${allowAnonymousUsers}`;
    if (selectedUserIds && selectedUserIds.length) {
      queryParams += `&selectedUserIds=${JSON.stringify(selectedUserIds)}`;
    } else {
      const searchParam = search ? `&search=${search}` : '';
      const courseAccessParam =
        courseAccess && courseAccess !== 'all'
          ? `&courseAccess=${courseAccess}`
          : '';

      queryParams +=
        courseAccessParam +
        searchParam +
        Object.keys(routerQueryParams || {})
          .map((key) => `&${key}=${routerQueryParams?.[key]}`)
          .join('');
    }

    const response = await api.get(baseUrl + queryParams);

    const identifiers: string[] = [];
    const userIds: { [key: string]: number } = {};
    const users: UserWithBasicData[] = [];
    for (let i = 0; i < response.data.users.length; i++) {
      const user = response.data.users[i];
      if (
        (userSelector === 'usernames' && excludeUsersWithEmail && user.email) ||
        (userSelector === 'emails' && excludeUsersWithoutEmail && !user.email)
      ) {
        continue;
      }

      if (excludeInvalidAccounts && isUserAccountLockedOrExpired({ user })) {
        continue;
      }

      const identifier = userSelector === 'emails' ? user.email : user.username;
      identifiers.push(identifier);
      userIds[identifier] = user.id;
      users.push(user);
    }

    return { error: false, identifiers, userIds, users };
  } catch (err) {
    return { error: true, identifiers: [], userIds: {}, users: [] };
  }
};

export default getSelectedUsers;
